import { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Chip,
    Pagination,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';

import { useLocales } from 'src/locales';
import { titleCase } from 'src/utils/change-case';

import { TextAdItem } from '../types';
import { domainFavicon } from '../utils';
import SerpModalButton from './SerpModalButton';
import CustomTooltip from './CustomTooltip';

interface Props {
    items: TextAdItem[];
    pageSize?: number;
    onlyTextAds?: boolean;
    showBrandChip?: boolean;
}

export default function TextAdsList({
    items,
    pageSize = 8,
    onlyTextAds = false,
    showBrandChip = false,
}: Props) {
    const [page, setPage] = useState(1);
    const { t } = useLocales();

    useEffect(() => {
        setPage(1);
    }, [items]);

    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const start = (page - 1) * pageSize;
    const current = items.slice(start, start + pageSize);

    return (
        <Stack
            spacing={1.5}
            sx={{
                width: '100%',
                minWidth: 0,
            }}
        >
            {/* Section header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography
                    sx={{
                        color: '#333C47',
                        fontFamily: '"Public Sans", sans-serif',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '120%',
                    }}
                >
                    {titleCase(
                        t('dashboard.global.visibility.text_ads')
                    )}
                </Typography>

                <Typography
                    sx={{
                        color: '#637381',
                        fontFamily: '"Public Sans", sans-serif',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '120%',
                    }}
                >
                    {items.length}{' '}
                    {t('dashboard.global.visibility.results_found')}
                </Typography>
            </Stack>

            {items.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: 'background.neutral',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {titleCase(
                            t(
                                'dashboard.global.visibility.no_txt_ads_found_for_this_country'
                            )
                        )}
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* Two-column Text Ads grid */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: onlyTextAds
                                ? {
                                    xs: '1fr',
                                    lg: 'repeat(2, minmax(0, 1fr))',
                                }
                                : '1fr',
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        {current.map((ad, idx) => (
                            <Paper
                                key={`${ad.domain}-${ad.searchTerm}-${idx}`}
                                variant="outlined"
                                sx={{
                                    backgroundColor: 'white',
                                    minWidth: 0,
                                    height: '100%',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {/* Main card content */}
                                <Stack
                                    spacing={1}
                                    sx={{
                                        p: 1.5,
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {/* Domain + metrics */}
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={1}
                                    >
                                        {/* Domain */}
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            sx={{
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            <Avatar
                                                src={domainFavicon(ad.domain)}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    backgroundColor: '#fff',
                                                    flexShrink: 0,
                                                }}
                                            />

                                            <Typography
                                                title={ad.domain}
                                                sx={{
                                                    color: '#333C47',
                                                    fontFamily:
                                                        '"Public Sans", sans-serif',
                                                    fontSize: '14px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 400,
                                                    lineHeight: '120%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: 0,
                                                }}
                                            >
                                                {ad.domain}
                                            </Typography>

                                            <Tooltip
                                                title={`Search term: ${ad.searchTerm}`}
                                                placement="top"
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        flexShrink: 0,
                                                        cursor: 'help',
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                    >
                                                        <rect
                                                            width="16"
                                                            height="16"
                                                            rx="8"
                                                            fill="white"
                                                        />

                                                        <path
                                                            d="M8.00016 10.6673V8.00065M8.00016 5.33398H8.00683M14.6668 8.00065C14.6668 11.6825 11.6821 14.6673 8.00016 14.6673C4.31826 14.6673 1.3335 11.6825 1.3335 8.00065C1.3335 4.31875 4.31826 1.33398 8.00016 1.33398C11.6821 1.33398 14.6668 4.31875 14.6668 8.00065Z"
                                                            stroke="#5E6D7B"
                                                            strokeWidth="1.33"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </Box>
                                            </Tooltip>
                                        </Stack>

                                        {/* Position and frequency */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={0.75}
                                            >
                                                <Tooltip
                                                    arrow
                                                    placement="top"
                                                    title={t(
                                                        'definitions.position'
                                                    )}
                                                >
                                                    <Stack display={"flex"} direction={"row"} gap={"4px"}>
                                                        <Typography
                                                            sx={{
                                                                color: '#919EAB',
                                                                fontFamily:
                                                                    '"Public Sans", sans-serif',
                                                                fontSize: '14px',
                                                                fontStyle: 'normal',
                                                                fontWeight: 400,
                                                                lineHeight: '140%',
                                                                cursor: 'help',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {t(
                                                                'dashboard.global.visibility.Position'
                                                            )}
                                                        </Typography>
                                                        <svg style={{ position: 'relative', top: 3, cursor: 'help' }} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <g clip-path="url(#clip0_6211_9570)">
                                                                <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#637381" stroke-linecap="round" stroke-linejoin="round" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_6211_9570">
                                                                    <rect width="12" height="12" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                    </Stack>
                                                </Tooltip>

                                                <Typography
                                                    sx={{
                                                        color: '#637381',
                                                        fontFamily:
                                                            '"Public Sans", sans-serif',
                                                        fontSize: '14px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 500,
                                                        lineHeight: '140%',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    #{ad.position}
                                                </Typography>
                                            </Stack>

                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={0.75}
                                            >
                                                <Tooltip
                                                    arrow
                                                    placement="top"
                                                    title={t(
                                                        'definitions.frequency'
                                                    )}
                                                >
                                                    <Stack display={"flex"} direction={"row"} gap={"4px"}>
                                                        <Typography
                                                            sx={{
                                                                color: '#919EAB',
                                                                fontFamily:
                                                                    '"Public Sans", sans-serif',
                                                                fontSize: '14px',
                                                                fontStyle: 'normal',
                                                                fontWeight: 400,
                                                                lineHeight: '140%',
                                                                cursor: 'help',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {t(
                                                                'dashboard.global.visibility.frequency'
                                                            )}
                                                        </Typography>
                                                        <svg style={{ position: 'relative', top: 3, cursor: 'help' }} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <g clip-path="url(#clip0_6211_9570)">
                                                                <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#637381" stroke-linecap="round" stroke-linejoin="round" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_6211_9570">
                                                                    <rect width="12" height="12" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                    </Stack>
                                                </Tooltip>

                                                <Typography
                                                    sx={{
                                                        color: '#637381',
                                                        fontFamily:
                                                            '"Public Sans", sans-serif',
                                                        fontSize: '14px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 500,
                                                        lineHeight: '140%',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {ad.frequency}%
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>

                                    {/* Ad title */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography
                                            title={ad.title}
                                            sx={{
                                                color: '#333C47',
                                                fontFamily:
                                                    '"Public Sans", sans-serif',
                                                fontSize: '16px',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '24px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                minWidth: 0,
                                            }}
                                        >
                                            {ad.title}
                                        </Typography>

                                        {showBrandChip &&
                                            (
                                                ad as TextAdItem & {
                                                    brandInfringementTitle?: boolean;
                                                }
                                            ).brandInfringementTitle && (
                                                <Chip
                                                    label="Brand"
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                    </Box>

                                    {/* Ad description */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography
                                            title={ad.description}
                                            sx={{
                                                color: '#858A91',
                                                fontFamily: '"Public Sans", sans-serif',
                                                fontSize: '13px',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '18px',
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                textOverflow: 'ellipsis',
                                                minWidth: 0,
                                            }}
                                        >
                                            {ad.description}
                                        </Typography>

                                        {showBrandChip &&
                                            (
                                                ad as TextAdItem & {
                                                    brandInfringementDescription?: boolean;
                                                }
                                            )
                                                .brandInfringementDescription && (
                                                <Chip
                                                    label="Brand"
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                    </Box>
                                </Stack>

                                {/* Card footer */}
                                <Box
                                    sx={{
                                        minHeight: 32,
                                        borderTop:
                                            '1px solid rgba(145, 158, 171, 0.20)',
                                        background: '#F8F9FA',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 0.5,
                                    }}
                                >
                                    <SerpModalButton
                                        gcsUrl={
                                            ad.device_type === 'MOBILE'
                                                ? (
                                                    ad as TextAdItem & {
                                                        _gcs_uri_mobile?: string;
                                                    }
                                                )._gcs_uri_mobile
                                                : (
                                                    ad as TextAdItem & {
                                                        _gcs_uri_desktop?: string;
                                                    }
                                                )._gcs_uri_desktop
                                        }
                                        deviceType={ad.device_type}
                                        iconType={
                                            ad.device_type === 'MOBILE'
                                                ? 'mobile'
                                                : 'desktop'
                                        }
                                        searchTerm={ad.searchTerm}
                                    />
                                </Box>
                            </Paper>
                        ))}
                    </Box>

                    {pageCount > 1 && (
                        <Stack
                            direction="row"
                            justifyContent="center"
                            sx={{ pt: 1 }}
                        >
                            <Pagination
                                color="standard"
                                size="small"
                                count={pageCount}
                                page={page}
                                onChange={(_, newPage) =>
                                    setPage(newPage)
                                }
                            />
                        </Stack>
                    )}
                </>
            )}
        </Stack>
    );
}